import { inject, Injectable, signal, WritableSignal } from "@angular/core";
import { OrdersRemoteService } from "@modules/limit-orders/remotes/orders.remote.service";
import { BehaviorSubject, combineLatest, Observable, of } from "rxjs";
import { catchError, filter, map, switchMap } from "rxjs/operators";
import { IOrderCount } from "@modules/limit-orders/interfaces/limit-orders.remote.interface";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import {
	IFFinCount,
	IFollowingCombinedFF,
	IFollowingCountData,
	InputData
} from "@modules/limit-orders/interfaces/limit-order-following.model";
import { EXPIRE_TIME } from "@modules/limit-orders/services/orders-widgets-following.facade";
import { resetTimer } from "@helpers/functions";

const initialState: IOrderCount = {
	opened: null,
	closed: null,
	total: null,
	done: null,
	maxOpenOrders: 0
};

const initialOrdersCountData: InputData<IFollowingCountData> & IFFinCount = {
	data: { buyOrders: 0, sellOrders: 0, totalOrders: 0, executedOrders: 0, cancelledOrders: 0, max: false },
	ff: { buyEnabled: false, sellEnabled: false, totalEnabled: false },
	error: false,
	cached: false,
	expires: resetTimer(EXPIRE_TIME)
};

@Injectable({ providedIn: "root" })
export class OrdersCountFacade {
	private readonly launchDarkly = inject(LaunchDarklyService);
	private readonly ordersRemote = inject(OrdersRemoteService);

	ordersCountSignal$: WritableSignal<InputData<IFollowingCountData> & IFFinCount> = signal(initialOrdersCountData);

	private ordersCount$$ = new BehaviorSubject<IOrderCount>(initialState);
	public getOrdersCount$(): Observable<IOrderCount> {
		return this.ordersCount$$.asObservable().pipe(filter((count) => count.maxOpenOrders !== 0));
	}

	public fetchOrdersCount(): void {
		combineLatest([
			this.launchDarkly.isActive$(LD_FLAG.LIMIT_ORDERS_BUY),
			this.launchDarkly.isActive$(LD_FLAG.LIMIT_ORDERS_SELL)
		])
			.pipe(
				switchMap(([isBuyEnabled, isSellEnabled]) => {
					if (isBuyEnabled || isSellEnabled) {
						return this.ordersRemote.getOrderCount$().pipe(
							map((count) => {
								this.ordersCount$$.next(count);
								const data = {
									buyOrders: count.opened.buy,
									sellOrders: count.opened.sell,
									totalOrders: count.opened.buy + count.opened.sell,
									executedOrders: count.done.buy + count.done.sell,
									cancelledOrders: count.closed.buy + count.closed.sell,
									max: count.opened.buy + count.opened.sell >= count.maxOpenOrders
								};
								const combinedFF: IFollowingCombinedFF = {
									buyEnabled: isBuyEnabled,
									sellEnabled: isSellEnabled,
									totalEnabled: true
								};
								this.ordersCountSignal$.set({
									data,
									ff: combinedFF,
									error: false,
									cached: true,
									expires: resetTimer(EXPIRE_TIME)
								});
								return count;
							}),
							catchError(() => {
								this.ordersCount$$.next(initialState);
								return of(initialState);
							})
						);
					}
				})
			)
			.subscribe();
	}
}
